import * as RadioGroupPrimitive from "@radix-ui/react-radio-group"
import { Circle } from "lucide-react"
import * as React from "react"

import { cn } from "@/lib/utils"
import { Control, FieldValues, Path, useController } from "react-hook-form"

const RadioGroup = React.forwardRef<
  React.ElementRef<typeof RadioGroupPrimitive.Root>,
  React.ComponentPropsWithoutRef<typeof RadioGroupPrimitive.Root>
>(({ className, ...props }, ref) => {
  return (
    <RadioGroupPrimitive.Root
      className={cn("grid gap-2", className)}
      {...props}
      ref={ref}
    />
  )
})
RadioGroup.displayName = RadioGroupPrimitive.Root.displayName

const RadioGroupItem = React.forwardRef<
  React.ElementRef<typeof RadioGroupPrimitive.Item>,
  React.ComponentPropsWithoutRef<typeof RadioGroupPrimitive.Item>
>(({ className, ...props }, ref) => {
  return (
    <RadioGroupPrimitive.Item
      ref={ref}
      className={cn(
        "border-primary text-primary ring-offset-background focus-visible:ring-ring aspect-square h-4 w-4 rounded-full border focus:outline-hidden focus-visible:ring-2 focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50",
        className,
      )}
      {...props}
    >
      <RadioGroupPrimitive.Indicator className="flex items-center justify-center">
        <Circle className="h-2.5 w-2.5 fill-current text-current" />
      </RadioGroupPrimitive.Indicator>
    </RadioGroupPrimitive.Item>
  )
})
RadioGroupItem.displayName = RadioGroupPrimitive.Item.displayName

function ControlledRadioGroup<TFieldValues extends FieldValues>({
  name,
  control,
  rules,
  onChange,
  onBlur,
  ...props
}: Omit<React.ComponentProps<typeof RadioGroup>, "value" | "onValueChange"> & {
  name?: Path<TFieldValues>
  control?: Control<TFieldValues>
  rules?: object
}) {
  const { field } = useController({ name: name!, control, rules })
  return (
    <RadioGroup
      name={name}
      value={field.value ?? ""}
      onValueChange={(value) => field.onChange(value)}
      {...props}
    />
  )
}

export { RadioGroup, RadioGroupItem, ControlledRadioGroup }
